import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Plus, Lightbulb, ArrowRight, Calculator } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAdvisor } from "@/advisor/AdvisorContext";
import { Icon } from "@/advisor/icons";
import { http, fetchEstimate } from "@/advisor/api";

const Section = ({ title, children }) => (
  <div className="py-4 border-b border-white/[0.06] last:border-0">
    <div className="adv-eyebrow mb-2">{title}</div>
    {children}
  </div>
);

const Upsell = () => {
  const { state, update, toggleSelect } = useAdvisor();
  const [tip, setTip] = useState(null);

  useEffect(() => {
    if (!state.selected.length) return setTip(null);
    http.post("/advisor/upsell", { slugs: state.selected, dismissed: state.dismissed }).then((r) => setTip(r.data.suggestion ? r.data : null));
  }, [state.selected, state.dismissed]);

  if (!tip) return null;
  const accept = () => {
    update({ extraRecommended: [...new Set([...state.extraRecommended, tip.suggestion.slug])] });
    toggleSelect(tip.suggestion.slug);
  };
  return (
    <div className="adv-card p-5 border-amber-400/30 bg-amber-400/[0.05] mt-4" data-testid="upsell-card">
      <div className="flex items-center gap-2 font-semibold text-amber-200"><Lightbulb className="h-5 w-5" /> LiveMedia tövsiyəsi</div>
      <p className="text-sm text-[#E9E3D8] mt-2" data-testid="upsell-text">{tip.text}</p>
      <div className="flex gap-2 mt-4">
        <button className="adv-btn-base bg-[#D6AD62] text-[#111110] px-5 text-sm" onClick={accept} data-testid="upsell-accept"><Plus className="h-4 w-4" /> Əlavə et</button>
        <button className="adv-btn-ghost text-sm" onClick={() => update({ dismissed: [...state.dismissed, tip.suggestion.slug] })} data-testid="upsell-dismiss">Lazım deyil</button>
      </div>
    </div>
  );
};

const AddMore = ({ open, onClose }) => {
  const { state, catalog, toggleSelect } = useAdvisor();
  const rest = catalog.filter((c) => !state.selected.includes(c.slug) && (state.business?.key === "tiktok") === c.suitable_for.includes("tiktok"));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden bg-[#151513] text-[#F3EEE4] border-white/10" data-testid="add-more-modal">
        <DialogHeader><DialogTitle className="font-display">Digər həllər</DialogTitle></DialogHeader>
        <div className="space-y-2 min-w-0">
          {rest.map((c) => (
            <button key={c.slug} onClick={() => toggleSelect(c.slug)} className="adv-option w-full p-3 flex items-center gap-3" data-testid={`add-more-${c.slug}`}>
              <Icon name={c.icon} className="h-5 w-5 text-[#F3D592] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-[#a9a398] truncate">{c.short_desc}</div>
              </div>
              <Plus className="h-4 w-4 text-[#F3D592]" />
            </button>
          ))}
          {!rest.length && <p className="text-sm text-[#a9a398]">Bütün həllər artıq seçilib.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Project() {
  const { state, meta, bySlug, toggleSelect } = useAdvisor();
  const [more, setMore] = useState(false);
  const [est, setEst] = useState(0);
  useEffect(() => { fetchEstimate(state.selected).then(setEst); }, [state.selected]);
  const nav = useNavigate();
  const goalLabels = (state.business?.key === "tiktok" ? meta?.tiktok_games : meta?.goals)
    ?.filter((g) => (state.business?.key === "tiktok" ? state.tiktokGames : state.goals).includes(g.key) && g.key !== "all").map((g) => g.label) || [];

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-6 pt-[110px] pb-24" data-testid="project-page">
      <div className="eyebrow mb-3">Layihəm</div>
      <h1 className="text-3xl sm:text-5xl font-semibold">Sizin rəqəmsal layihəniz</h1>

      <div className="adv-card p-5 sm:p-6 mt-6">
        <Section title="Biznes">
          {state.business ? (
            <div className="flex items-center gap-2 text-lg font-semibold" data-testid="project-business">
              <Icon name={state.business.icon} className="h-5 w-5 text-[#F3D592]" /> {state.business.label}
            </div>
          ) : <Link to="/advisor" className="text-[#F3D592] text-sm">Biznesinizi təsvir edin →</Link>}
        </Section>
        {goalLabels.length > 0 && (
          <Section title="Məqsəd">
            <ul className="space-y-1 text-[#E9E3D8]" data-testid="project-goals">{goalLabels.map((g) => <li key={g}>{g}</li>)}</ul>
          </Section>
        )}
        <Section title="Seçilmiş həllər">
          <div className="space-y-2">
            {state.selected.map((slug) => (
              <div key={slug} className="flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/[0.06] p-3" data-testid={`project-item-${slug}`}>
                <Icon name={bySlug[slug]?.icon} className="h-5 w-5 text-[#D6AD62]" />
                <span className="flex-1 font-medium">{bySlug[slug]?.name || slug}</span>
                <button onClick={() => toggleSelect(slug)} className="h-8 w-8 rounded-full hover:bg-rose-500/20 text-[#a9a398] hover:text-rose-300 grid place-items-center" data-testid={`btn-remove-project-item-${slug}`}>
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            {!state.selected.length && <p className="text-sm text-[#a9a398]" data-testid="project-empty">Hələ heç bir həll seçilməyib.</p>}
          </div>
          <button className="adv-btn-ghost w-full mt-3 text-sm" onClick={() => setMore(true)} data-testid="btn-add-more-solutions"><Plus className="h-4 w-4" /> Digər həllər</button>
        </Section>
      </div>

      <Upsell />

      <div className="adv-card p-6 mt-4 bg-gradient-to-br from-[#D6AD62]/10 to-[#B98B42]/10" data-testid="price-block">
        <div className="flex items-center gap-2"><Calculator className="h-5 w-5 text-[#F3D592]" /><h3 className="text-xl font-bold">Layihənin qiymətini öyrən</h3></div>
        {est > 0 && (
          <div className="mt-3" data-testid="project-estimate">
            <div className="text-3xl font-display font-bold text-[#F3D592]">{est} ₼-dan</div>
            <p className="text-xs text-[#a9a398] mt-1">Təxmini başlanğıc qiymət. Dəqiq məbləğ layihənin detallarına görə təklifdə göstəriləcək.</p>
          </div>
        )}
        <p className="text-sm text-[#cfc9bd] mt-2">Seçdiyiniz funksiyalara əsasən layihənin qiymətini hesablayacağıq.</p>
        <button className="adv-btn w-full mt-5 text-base" disabled={!state.selected.length} onClick={() => nav("/advisor/teklif")} data-testid="btn-get-quote">
          Qiymət təklifi al <ArrowRight className="h-5 w-5" />
        </button>
      </div>
      <AddMore open={more} onClose={() => setMore(false)} />
    </div>
  );
}
